import { CallEmsApi, EmsApiUrl, iEmsApiResponseBody, networkFailure } from "../Logic/TenantAPI";
import { ACTION_FOR, EmailAction } from "./emailActions";
import { EmailRef, MailOrigin, ProcessingStatus } from "./EmailSearchAPI";

/**
 * Every stage the pipeline journals, across both directions.
 *
 * Fifteen values for two pipelines: `Received`/`Extracted`/`EnquiryCreated`/
 * `EnquiryMatched`/`MessageLinked`/`AutoResponseTriggered`/`Completed` are
 * inbound, `Queued`/`Built`/`Sendable`/`SendAttempted`/`Sent` outbound, and
 * `Failed`/`DeadLettered`/`Reprocessed` occur on either. The view renders the
 * name verbatim rather than mapping it, so a stage added server-side shows up
 * as itself instead of disappearing.
 */
export type ProcessingStage =
    | "Received"
    | "Extracted"
    | "EnquiryCreated"
    | "EnquiryMatched"
    | "MessageLinked"
    | "AutoResponseTriggered"
    | "Completed"
    | "Failed"
    | "DeadLettered"
    | "Reprocessed"
    | "Queued"
    | "Built"
    | "Sendable"
    | "SendAttempted"
    | "Sent";

export type ProcessingOutcome = "Started" | "Succeeded" | "Failed" | "DeadLettered" | "Reprocessed";

export interface ProcessingTimelineEntry {
    stage: ProcessingStage;
    outcome: ProcessingOutcome;
    occurredAt: string;
    failureReason?: string;
    /** Unbounded. The view collapses and caps it. */
    failureStackTrace?: string;
    /** Send entries only. Absent means "not a send attempt", not "unknown". */
    attempt?: number;
    /**
     * THE MARKER FOR A HUMAN, and the reason this is not `reason`.
     *
     * The contract says `reason` distinguishes a support-triggered entry from
     * an automatic one. It cannot: `reason` is optional on both POSTs, so a
     * support action taken without a note has none. Both handlers pass
     * `CurrentUser.ID` unconditionally, so this is present on every
     * support-triggered entry and absent on every automatic one (spec §3.3).
     */
    triggeredByUserID?: number;
    /** The optional free-text audit note, when one was given. */
    reason?: string;
}

interface EmailDetailBase {
    ref: EmailRef;
    mailboxID?: number;
    /** Nullable on the wire, on both shapes. */
    status?: ProcessingStatus;
    /** Server-computed at read time. Never re-derive it (contract §2.3). */
    stalled: boolean;
    subject?: string;
    from?: string;
    to?: string;
    processingUpdatedAt?: string;
    lastFailureReason?: string;
    /** No enquiry view exists in this app, so this renders as a value, not a link. */
    enquiryID?: number;
    /** Oldest to newest, ordered by the server. Never re-sorted here. */
    timeline?: ProcessingTimelineEntry[];
    asOf: string;
}

export interface InboundEmailDetail extends EmailDetailBase {
    receivedAt?: string;
    hasAttachments: boolean;
}

export interface OutboundEmailDetail extends EmailDetailBase {
    origin?: MailOrigin;
    queuedAt?: string;
    sentAt?: string;
    attemptCount?: number;
}

export type EmailDetail = InboundEmailDetail | OutboundEmailDetail;

/**
 * Narrows the detail union.
 *
 * `ref.direction` is a NESTED discriminant, and TypeScript 4.9 narrows unions
 * only on a direct property. A plain `detail.ref.direction === "Inbound"`
 * check compiles but narrows nothing, leaving every inbound-only field
 * unreachable. Same shape, and same reason, as `isInbound` for the list rows.
 */
export function isInboundDetail(detail: EmailDetail): detail is InboundEmailDetail {
    return detail.ref.direction === "Inbound";
}

/**
 * One email, with its timeline.
 *
 * The route is built from `ref` AS A UNIT (contract §2.7): `id` means
 * `dataLinkID` for inbound and `messageID` for outbound, and the two occupy
 * wildly different ranges — 9733 against 100192427 in the dev tenant — so a
 * consumer that picked an id field per direction would look correct on one
 * and be silently wrong on the other.
 *
 * `CallEmsApi` has no try/catch around its own `fetch` or `response.json()`,
 * so it rejects on a network error or a malformed body. This wrapper converts
 * that into a status-0 response so callers have one shape to handle.
 */
export async function GetEmailDetail(
    tenantScopedToken: string,
    ref: EmailRef,
): Promise<iEmsApiResponseBody<EmailDetail>> {
    try {
        return await CallEmsApi<EmailDetail>(tenantScopedToken, "GET", `MailProcessing/${ref.direction}/${ref.id}`);
    } catch {
        return networkFailure<EmailDetail>();
    }
}

/**
 * The single entry journaled by the action just taken.
 *
 * A strict subset of `ProcessingTimelineEntry` — no `failureReason`,
 * `failureStackTrace` or `attempt`, because a queued action has failed
 * nothing and attempted nothing. Structurally assignable to it, which is what
 * lets the optimistic apply push this straight onto `timeline`.
 */
export interface ActionEvent {
    stage: ProcessingStage;
    outcome: ProcessingOutcome;
    occurredAt: string;
    triggeredByUserID?: number;
    reason?: string;
}

/**
 * ACCEPTED, NOT DONE (contract §2.8).
 *
 * A `200` means the work has been QUEUED. The email has not been reprocessed
 * or resent when this arrives, and a UI that reports "retried successfully"
 * on it is lying. `newStatus` is the flip to reflect immediately and `event`
 * is the entry just journaled; the eventual outcome arrives only by re-reading
 * the detail.
 */
export interface ActionAccepted {
    ref: EmailRef;
    action: EmailAction;
    /**
     * Reprocess is always `Queued`. Retry is `Queued` for a rebuild and
     * `Sendable` for an `Abandoned` re-arm — which is the ONLY place the two
     * mechanisms are distinguishable, since both journal an identical
     * `Reprocessed`/`Reprocessed` pair (spec §3.4).
     */
    newStatus: ProcessingStatus;
    event: ActionEvent;
    asOf: string;
}

/**
 * Queue this email's support action.
 *
 * One function for both, because the route is fixed by direction and the
 * response shape is identical. `ACTION_FOR` is the same map the eligibility
 * rules use, so the button's label, its rule and this path can never disagree
 * about which action an email offers.
 *
 * The reason is OPTIONAL and stays optional: the contract says to prompt for
 * one and not to require it. A blank or whitespace-only note is sent as no
 * note at all rather than as an empty string, so the timeline does not gain
 * an entry quoting "". `undefined` is dropped by `JSON.stringify`, so the body
 * goes out as `{}` — which the endpoint accepts.
 */
export async function SendEmailAction(
    tenantScopedToken: string,
    ref: EmailRef,
    reason?: string,
): Promise<iEmsApiResponseBody<ActionAccepted>> {
    const note = reason?.trim();
    try {
        return await CallEmsApi<ActionAccepted>(
            tenantScopedToken,
            "POST",
            `MailProcessing/${ref.direction}/${ref.id}/${ACTION_FOR[ref.direction]}`,
            { reason: note ? note : undefined },
        );
    } catch {
        return networkFailure<ActionAccepted>();
    }
}

/**
 * Why a `.eml` download did not produce a file.
 *
 * One per row of the brief's status table, plus `failed` for a status the
 * table does not name, so an unexpected `502` is not described as a missing
 * stored object.
 */
export type EmlDownloadFailure = "unauthorised" | "forbidden" | "notFound" | "unreadable" | "interrupted" | "failed";

export type EmlDownloadResult = { kind: "saved"; filename: string } | { kind: "failed"; reason: EmlDownloadFailure };

function emlFailureForStatus(status: number): EmlDownloadFailure {
    switch (status) {
        case 401:
            return "unauthorised";
        case 403:
            return "forbidden";
        case 404:
            return "notFound";
        case 500:
            return "unreadable";
        default:
            return "failed";
    }
}

/**
 * The file name to save under: the server's, when it sent one, otherwise
 * `{direction}-{id}.eml` ï¿½ the same name the server would have sent.
 *
 * Takes `filename*=` (RFC 5987) over `filename=`, as a browser would. Any
 * path separator is stripped, so a header can only ever name a file, never
 * a location.
 */
export function emlFilename(contentDisposition: string | null, ref: EmailRef): string {
    const fallback = `${ref.direction.toLowerCase()}-${ref.id}.eml`;
    if (!contentDisposition) {
        return fallback;
    }

    let name: string | undefined;
    const extended = /filename\*\s*=\s*(?:[\w-]+'[^']*')?([^;]+)/i.exec(contentDisposition);
    if (extended) {
        try {
            name = decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ""));
        } catch {
            name = undefined;
        }
    }
    if (!name) {
        const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(contentDisposition);
        name = plain ? (plain[2] ?? plain[1]).trim() : undefined;
    }

    const safe = name?.replace(/[\\/]/g, "").trim();
    return safe ? safe : fallback;
}

/**
 * Hands the browser a file to save. Revoked on the next tick rather than
 * immediately, because some browsers read the object URL asynchronously
 * after `click()` returns.
 */
function saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Download this email's original (inbound) or built (outbound) `.eml`.
 *
 * NOT `CallEmsApi`: that reads every `200` as JSON. The route is built from
 * `ref` as a unit (contract ï¿½2.7), the same as `GetEmailDetail`.
 *
 * DO NOT GUNZIP. The response is `Content-Encoding: gzip`, which `fetch`
 * undoes by itself, so `blob()` is already the plain message. That is the
 * opposite of `Mail/{commId}/download`, whose bytes the client unzips; doing
 * the same here would fail on data that is no longer compressed.
 *
 * The status is checked BEFORE the body is read, so an error body is never
 * saved as a `.eml`. A `403`/`404` body is empty and a `500` body is
 * ProblemDetails; none of them is parsed. With no `Content-Length`, a fault
 * partway through a large file shows up as `blob()` rejecting, not as a
 * status ï¿½ hence `interrupted`, which a failed `fetch` shares, since both
 * mean "the transfer broke, try again".
 */
export async function DownloadEmailEml(tenantScopedToken: string, ref: EmailRef): Promise<EmlDownloadResult> {
    let response: Response;
    try {
        response = await fetch(EmsApiUrl(`MailProcessing/${ref.direction}/${ref.id}/Eml`), {
            method: "GET",
            headers: { Authorization: "bearer " + tenantScopedToken },
        });
    } catch {
        return { kind: "failed", reason: "interrupted" };
    }

    if (!response.ok) {
        return { kind: "failed", reason: emlFailureForStatus(response.status) };
    }

    let blob: Blob;
    try {
        blob = await response.blob();
    } catch {
        return { kind: "failed", reason: "interrupted" };
    }

    const filename = emlFilename(response.headers.get("Content-Disposition"), ref);
    saveBlob(blob, filename);
    return { kind: "saved", filename };
}
